import React, { forwardRef, useState, useEffect, useContext } from 'react'
import {
  Dialog,
  DialogContent,
  IconButton,
  Grow,
  Box,
  Typography,
  Button,
  Grid,
} from '@mui/material'
import { motion } from 'framer-motion'
import { Close } from '@mui/icons-material'
import { AnimatePresence } from 'framer-motion'
import { callZapier } from 'utils/zapier/callZapier'
import { IntakeContext } from 'context/IntakeContext'
import { useFormDebounce } from 'hooks/useFormDebounce'

const Transition = forwardRef(function Transition(props, ref) {
  return (
    <Grow
      in={props.in}
      timeout={{ enter: 300, exit: 300 }}
      ref={ref}
      {...props}
    />
  )
})

export default function WebinarSignup({ onClose }) {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)
  const { updateIntakeStateAndSubmit } = useFormDebounce()
  const [page, setPage] = useState(0)
  const [direction, setDirection] = useState(1)
  const handleNext = () => {
    setDirection(1)
    setPage((prev) => Math.min(prev + 1, pages.length - 1))
  }
  const handleBack = () => {
    setDirection(-1)
    setPage((prev) => Math.max(prev - 1, 0))
  }
  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0,
    }),
  }

  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => {
    setHasMounted(true)
  }, [])

  const registerForWebinar = () => {
    const webinarSignup = {
      firstName: intakeState?.firstName || 'No First Name',
      lastName: intakeState?.lastName || 'No Last Name',
      email: intakeState?.email || 'No Email',
      phone: intakeState?.phone || 'No Phone',
    }
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/usf3rbi/',
      payload: {
        webinarSignup: webinarSignup,
      },
      notificationMessage: `You have successfully registered for the webinar`,
      sendNotification: true,
    })
    updateIntakeStateAndSubmit(() => {
      return updateIntakeStateAtKey({ webinarRegistered: true })
    })()
    handleNext()
  }

  const pages = [
    <Grid key="1" item xs={12}>
      <Box>
        <Typography variant="headerTitle" color="#000">
          Register for our upcoming Webinar on{' '}
          <span style={{ color: '#2370ED' }}>RMDs & Roth Conversions</span>
        </Typography>
        <Typography variant="body1" mt={4}>
          Join us on Tuesday, November 25 @ 7pm EST. Attend virtually on Zoom!
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'center' }} my={{ xs: 2, md: 4 }}>
        <img
          src="https://company-agency-logos.s3.us-east-2.amazonaws.com/images/movie.png"
          alt="signup"
          style={{ height: '200px', width: 'auto' }}
        />
      </Box>
    </Grid>,
    <Grid key="2" item xs={12}>
      <Box>
        <Typography variant="headerTitle" color="#000">
          You're registered! We'll see you soon!
        </Typography>
        <Typography variant="body1" mt={4}>
          We'll see you on Tuesday, November 25 @ 7pm EST. Expect an email with
          a Zoom link to the webinar.
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'center' }} my={{ xs: 2, md: 4 }}>
        <img
          src="https://company-agency-logos.s3.us-east-2.amazonaws.com/images/greencheck.png"
          alt="signup"
          style={{ height: '200px', width: 'auto' }}
        />
      </Box>
    </Grid>,
  ]

  return (
    <>
      <Dialog
        open
        onClose={onClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth={'md'}
        PaperProps={{
          sx: {
            overflow: { xs: 'auto', sm: 'visible' },
            margin: '16px',
            width: 'calc(100% - 32px)',
            '@media (max-height:960px)': {
              overflow: 'auto',
            },
          },
        }}
      >
        <Box
          sx={{
            justifyContent: 'space-between',
            position: 'relative',
            display: 'flex',
            top: { xs: 8, sm: -48 },
            '@media (max-height:960px)': {
              top: 8,
            },
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 0,
              right: { xs: 8, sm: 0 },
              color: '#fff',
              zIndex: 1,
              backgroundColor: '#000',
              '&:hover': {
                backgroundColor: '#000',
              },
              '@media (max-height:960px)': {
                right: 8,
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
        <DialogContent sx={{ overflow: 'hidden' }}>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={page}
              custom={direction}
              variants={variants}
              initial={hasMounted ? 'enter' : false}
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              style={{
                position: 'relative',
                width: '100%',
              }}
            >
              <Box
                px={{ xs: 0, sm: 6 }}
                pt={{ xs: 4, sm: 6 }}
                pb={{ xs: 2, sm: 6 }}
              >
                {pages[page]}
                {page === 0 ? (
                  <Box justifyContent="space-between" display="flex" gap={1}>
                    <Button
                      variant="parcoRounded"
                      color="outline"
                      onClick={onClose}
                      rel="noreferrer"
                      href="https://www.goparco.com/webinars-1"
                      target="_blank"
                      sx={{
                        lineHeight: { xs: 1.2, sm: 1.5 },
                      }}
                    >
                      View past Webinars
                    </Button>
                    <Button
                      variant="parcoRounded"
                      onClick={registerForWebinar}
                      sx={{
                        lineHeight: { xs: 1.2, sm: 1.5 },
                      }}
                    >
                      Register for the Webinar
                    </Button>
                  </Box>
                ) : (
                  <Box justifyContent="space-between" display="flex" gap={1}>
                    <Button
                      variant="parcoRounded"
                      color="outline"
                      onClick={onClose}
                      rel="noreferrer"
                      href="https://www.goparco.com/webinars-1"
                      target="_blank"
                      sx={{
                        lineHeight: { xs: 1.2, sm: 1.5 },
                      }}
                    >
                      View past Webinars
                    </Button>
                    <Button
                      variant="parcoRounded"
                      onClick={onClose}
                      sx={{
                        lineHeight: { xs: 1.2, sm: 1.5 },
                      }}
                    >
                      Done
                    </Button>
                  </Box>
                )}
              </Box>
            </motion.div>
          </AnimatePresence>
        </DialogContent>
      </Dialog>
    </>
  )
}
